<template>
  <ErrorDisplay v-if="error" :message="error.message" :loading="loading" @retry="loadData" />
  <v-select
    v-else
    :variant="!showLabel ? 'solo' : undefined"
    :density="!showLabel ? 'compact' : 'default'"
    label="Niederlassung"
    :hide-details="hintText === ''"
    class="mt-3 mb-2"
    v-model="internalValue"
    item-title="Name1"
    item-value="NiederlassungNr"
    :hint="hintText"
    persistent-hint
    :items="loading ? [] : data"
    :loading="loading"
    placeholder="--- Niederlassung wählen ---"
    :return-object="false"
  >
  <template v-slot:item="{ props, item }">
    <v-list-item v-bind="props" :subtitle="item.raw.Bezeichnung"></v-list-item>
  </template>
  </v-select>
</template>

<script setup lang="ts">
import ErrorDisplay from './ErrorDisplay.vue';
import {getSubsidiaries} from '@/api/module/warehouse';
import Subsidiary from '@/model/Subsidiary';
import pinia from '@/plugins/pinia';
import {useSettings} from '@/store/settings';
import {computed, ref, watch} from 'vue';

const emits = defineEmits({
  'update:modelValue'(val: number) {
    return typeof val === 'number';
  },
});

const props = defineProps({
  showLabel: {
    type: Boolean,
    default: false,
  },
  useModel: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: Number,
    default: useSettings(pinia).selectedSubsidiary,
    required: false,
  },
  client: {
    type: Number,
    required: false,
  },
  customerId: {
    type: String,
    default: null,
  },
});

const loading = ref(true);
const data = ref<Subsidiary[]>([]);
const error = ref<Error | null>(null);

const settingsStore = useSettings();

const internalValue = computed({
  get() {
    if (props.useModel) return props.modelValue;

    return settingsStore.selectedSubsidiary!;
  },
  set(newVal: number) {
    if (props.useModel) {
      emits('update:modelValue', newVal);
      return;
    }

    settingsStore.selectedSubsidiary = newVal;
  },
});

const hintText = computed(()=> {
  if (!data.value || data.value.length === 0) return '';
  const selected = data.value.find((sub) => sub.NiederlassungNr === internalValue.value);
  if (!selected?.Bezeichnung || selected?.Bezeichnung === '') return '';
  return `${selected.Bezeichnung}`;
});

function loadData() {
  loading.value = true;
  if (props.client === undefined) {
    data.value = [];
    loading.value = false;
    error.value = null;
    return;
  }
  getSubsidiaries(props.client !== undefined ? {client: props.client, customerId: props.customerId} : undefined)
      .then(([d]) => {
        data.value = d;
        error.value = null;
      })
      .catch((e) => {
        error.value = e;
        data.value = [];
      })
      .finally(() => loading.value = false);
}

watch(() => props.client, () => {
  loadData();
}, {immediate: true});

defineExpose({
  ErrorDisplay,
  loading,
  data,
  error,
  internalValue,
  props,
  emits,
  loadData,
});
</script>
